import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Save, Eye, Send, AlertTriangle, CalendarClock, Ban, CheckCircle2, Users, Sparkles, Wand2, Plus, RefreshCw, FileText } from 'lucide-react';
import { EmailImagePanel } from './EmailImagePanel';
import VideoCandidatesNote from './VideoCandidatesNote';
import RolloutNote from './RolloutNote';
import IssueNotices, { statusLabel } from './IssueNotices';

const API = '/api'; // Same-origin: /api routes to backend on the serving domain.

const EMPTY = {
  id: null,
  subject: '',
  header_headline: '',
  header_body: '',
  header_image: null,
  tip_lead: '',
  tip_body: '',
  news_body: '',     // skippable — omitted from the email when empty
  win_body: '',      // skippable — omitted from the email when empty
  win_image: null,
  cta_text: '',
  cta_url: '',
  cta_blurb: '',
  sign_off_lead: '',
  sign_off: 'Henry-Griffitts',
};

function Field({ label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between">
        <label className="text-xs font-sans font-medium uppercase tracking-wider text-hg-text-dim">{label}</label>
        {hint && <span className="text-[10px] text-hg-text-dim font-sans">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

const inputCls =
  'w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm font-sans text-hg-text focus:border-hg-green focus:outline-none';

export default function WeeklyFitterComposer({ onNavigate }) {
  const [issue, setIssue] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [notice, setNotice] = useState('');
  // Scheduling (Level 2) state
  const [sendingTest, setSendingTest] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [sendAt, setSendAt] = useState('');           // datetime-local value
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [scheduleToTest, setScheduleToTest] = useState(false); // dry-run to HG Hub Test group
  const [target, setTarget] = useState(null);         // {configured, group_name, subscriber_count}

  // Draft list (seeded + AI-generated + saved), so any issue is reviewable/openable.
  const [drafts, setDrafts] = useState([]);
  // Single-issue AI generation.
  const [aiBrief, setAiBrief] = useState('');
  const [aiDrafting, setAiDrafting] = useState(false);
  // Batch AI generation.
  const [batchCount, setBatchCount] = useState(4);
  const [batchThemes, setBatchThemes] = useState('');
  const [batchGenerating, setBatchGenerating] = useState(false);
  // Test-send recipient (default Randall; type grant@henry-griffitts.com to add him).
  const [testEmail, setTestEmail] = useState('randall@henry-griffitts.com');

  const fetchDrafts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/email/weekly-fitter/list`);
      setDrafts(res.data.issues || []);
    } catch { /* list is a convenience; ignore failures */ }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/email/weekly-fitter/latest`);
        if (res.data.issue) setIssue({ ...EMPTY, ...res.data.issue });
      } catch (e) {
        console.error('load weekly fitter failed', e);
      }
      try {
        const t = await axios.get(`${API}/email/weekly-fitter/target-info`);
        setTarget(t.data);
      } catch { /* display-only; ignore */ }
      await fetchDrafts();
      setLoading(false);
    })();
  }, [fetchDrafts]);

  // Content fields whose edit must invalidate a prior test (mirrors the server
  // guardrail) so any change forces a fresh test before scheduling to fitters.
  const CONTENT_KEYS = [
    'subject', 'header_headline', 'header_body', 'header_image', 'tip_lead',
    'tip_body', 'news_body', 'win_body', 'win_image', 'cta_text', 'cta_url',
    'cta_blurb', 'sign_off_lead', 'sign_off',
  ];

  // Editing a SCHEDULED issue must cancel the queued send (it holds a stale
  // content snapshot). Fire-and-forget; the server save also enforces this.
  const autoCancelScheduled = async (id, cid) => {
    try {
      await axios.post(`${API}/email/weekly-fitter/cancel-schedule?issue_id=${encodeURIComponent(id)}`);
      flash('Content changed — scheduled send cancelled. Re-test, then reschedule.');
    } catch {
      flash('Edited a scheduled issue — could not auto-cancel the queued send; check MailerLite.');
    }
  };

  const set = (k, v) => {
    // On the first content edit of a scheduled issue, cancel the queued send.
    if (CONTENT_KEYS.includes(k) && issue[k] !== v && issue.status === 'scheduled' && issue.mailerlite_campaign_id) {
      autoCancelScheduled(issue.id, issue.mailerlite_campaign_id);
    }
    setIssue((cur) => {
      if (cur[k] === v) return cur;
      const next = { ...cur, [k]: v };
      if (CONTENT_KEYS.includes(k)) {
        if (cur.status === 'scheduled') {
          // Revert to draft immediately; the queued send is being cancelled.
          next.status = 'draft';
          next.scheduled_at = null;
          next.mailerlite_campaign_id = null;
          next.scheduled_test = false;
          next.test_sent_at = null;
          next.last_test_campaign_id = null;
        } else if (cur.test_sent_at) {
          // Editing invalidates the prior test (server resets it on save too).
          next.test_sent_at = null;
          next.last_test_campaign_id = null;
          if (cur.status === 'test_sent') next.status = 'draft';
        }
      }
      return next;
    });
  };

  const flash = (msg) => {
    setNotice(msg);
    setTimeout(() => setNotice(''), 4000);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Backend upserts on id; send null id to create, returned id is reused after.
      const payload = { ...issue };
      if (!payload.id) delete payload.id;
      const res = await axios.post(`${API}/email/weekly-fitter/save`, payload);
      setIssue({ ...EMPTY, ...res.data.issue });
      fetchDrafts();
      flash('Saved');
      return res.data.issue;
    } catch (e) {
      flash(e.response?.data?.detail || 'Save failed');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const preview = async () => {
    setPreviewing(true);
    try {
      const res = await axios.post(`${API}/email/weekly-fitter/preview`, issue);
      setPreviewHtml(res.data.html || '');
    } catch (e) {
      flash('Preview failed');
    }
    setPreviewing(false);
  };

  const push = async (test) => {
    if (!test && !window.confirm('Send a DRAFT to the live fitters group (2026 Fitter List USA)? You will still review and send it from the Mailerlite dashboard.')) {
      return;
    }
    setPushing(true);
    try {
      const saved = await save(); // ensure latest content + an id
      const id = saved?.id || issue.id;
      if (!id) { flash('Save first'); setPushing(false); return; }
      const res = await axios.post(`${API}/email/weekly-fitter/push?issue_id=${encodeURIComponent(id)}&test=${test}`);
      flash(`${test ? 'Test' : 'Fitters'} draft created in Mailerlite — review & send from the dashboard.`);
      if (res.data.mailerlite_url) window.open(res.data.mailerlite_url, '_blank');
    } catch (e) {
      flash(e.response?.data?.detail || 'Push failed');
    }
    setPushing(false);
  };

  const refreshLatest = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/email/weekly-fitter/latest`);
      if (res.data.issue) setIssue({ ...EMPTY, ...res.data.issue });
    } catch { /* keep current state */ }
    fetchDrafts();
  }, [fetchDrafts]);

  // Open any draft from the list into the composer (list returns full docs).
  const openDraft = (d) => {
    setIssue({ ...EMPTY, ...d });
    setPreviewHtml('');
    setShowSchedule(false);
    flash(`Opened: ${d.subject || '(untitled)'}`);
  };

  // Start a fresh blank issue.
  const newBlank = () => {
    setIssue(EMPTY);
    setPreviewHtml('');
    setShowSchedule(false);
    setAiBrief('');
    flash('New blank issue');
  };

  // Part 2 — single-issue AI generation. Fills the composer fields as an editable
  // draft (NOT saved/tested/sent). Keeps the current id so it updates the open
  // draft on Save, or stays new if the composer was blank.
  const aiDraft = async () => {
    setAiDrafting(true);
    try {
      const res = await axios.post(`${API}/email/weekly-fitter/ai-draft`, { brief: aiBrief || null });
      const f = res.data.fields || {};
      setIssue((cur) => ({
        ...cur,
        ...f,
        // Generated content is unsent: reset workflow so it must be tested before scheduling.
        status: 'draft', test_sent_at: null, last_test_campaign_id: null,
        scheduled_at: null, mailerlite_campaign_id: null, scheduled_test: false,
      }));
      const issues = res.data.lint?.issues;
      flash(issues ? `AI draft ready — ${issues} voice note(s) to review.` : 'AI draft ready — review & edit, then test.');
    } catch (e) {
      flash(e.response?.data?.detail || 'AI draft failed');
    }
    setAiDrafting(false);
  };

  // Part 3 — batch generation. Inserts N distinct Drafts; they appear in the list
  // for click-through review. Nothing is auto-tested/scheduled/sent.
  const runBatch = async () => {
    setBatchGenerating(true);
    try {
      const res = await axios.post(`${API}/email/weekly-fitter/ai-batch`, {
        count: batchCount,
        themes: batchThemes || null,
      });
      await fetchDrafts();
      const n = res.data.items_created || 0;
      flash(`Generated ${n} draft${n === 1 ? '' : 's'} — pick one below to review, edit & test.`);
    } catch (e) {
      flash(e.response?.data?.detail || 'Batch generation failed');
    }
    setBatchGenerating(false);
  };

  // datetime-local -> ISO with the user's local offset, so the server's
  // future-check is correct and MailerLite gets the right wall-clock time.
  const toOffsetISO = (local) => {
    const d = new Date(local);
    const pad = (n) => String(Math.abs(n)).padStart(2, '0');
    const tzMin = -d.getTimezoneOffset();
    const sign = tzMin >= 0 ? '+' : '-';
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
      `T${pad(d.getHours())}:${pad(d.getMinutes())}:00` +
      `${sign}${pad(Math.trunc(Math.abs(tzMin) / 60))}:${pad(Math.abs(tzMin) % 60)}`;
  };

  const sendTest = async () => {
    const rcpt = (testEmail || '').trim();
    setSendingTest(true);
    try {
      const saved = await save();
      const id = saved?.id || issue.id;
      if (!id) { flash('Save first'); setSendingTest(false); return; }
      const q = `issue_id=${encodeURIComponent(id)}` + (rcpt ? `&recipient_email=${encodeURIComponent(rcpt)}` : '');
      await axios.post(`${API}/email/weekly-fitter/send-test?${q}`);
      await refreshLatest();
      flash(rcpt ? `Test sent to ${rcpt} (via HG Hub Test group).` : 'Test sent to the HG Hub Test group.');
    } catch (e) {
      flash(e.response?.data?.detail || 'Test send failed');
    }
    setSendingTest(false);
  };

  const doSchedule = async () => {
    if (!sendAt) { flash('Pick a date & time'); return; }
    if (!confirmChecked) { flash('Confirm the send first'); return; }
    if (new Date(sendAt) <= new Date()) { flash('Pick a time in the future'); return; }
    setScheduling(true);
    try {
      const saved = await save();
      const id = saved?.id || issue.id;
      if (!id) { flash('Save first'); setScheduling(false); return; }
      const res = await axios.post(`${API}/email/weekly-fitter/schedule`, {
        issue_id: id,
        send_at: toOffsetISO(sendAt),
        confirm: true,
        test: scheduleToTest,
      });
      // Reflect scheduled state immediately from the response (don't depend on a
      // refetch) so the confirmation banner always appears.
      setIssue((cur) => ({
        ...cur,
        status: 'scheduled',
        scheduled_at: res.data.scheduled_at,
        scheduled_test: !!res.data.test,
        mailerlite_campaign_id: res.data.campaign_id,
      }));
      setShowSchedule(false);
      setConfirmChecked(false);
      setScheduleToTest(false);
      flash(scheduleToTest
        ? 'Test schedule created — see it on the Content Calendar.'
        : 'Scheduled to fitters — see it on the Content Calendar.');
    } catch (e) {
      flash(e.response?.data?.detail || 'Schedule failed');
    }
    setScheduling(false);
  };

  const cancelSchedule = async () => {
    if (!issue.id) return;
    if (!window.confirm('Cancel the scheduled send and return this issue to draft?')) return;
    setCancelling(true);
    try {
      await axios.post(`${API}/email/weekly-fitter/cancel-schedule?issue_id=${encodeURIComponent(issue.id)}`);
      await refreshLatest();
      flash('Scheduled send cancelled — back to draft.');
    } catch (e) {
      flash(e.response?.data?.detail || 'Cancel failed');
    }
    setCancelling(false);
  };

  const status = issue.status || 'draft';
  const hasTested = !!issue.test_sent_at;
  const isScheduled = status === 'scheduled';
  const targetName = target?.group_name || 'Weekly Fitter — All Fitters';
  const fmt = (s) => { try { return new Date(s).toLocaleString(); } catch { return s; } };
  const draftStatus = statusLabel;
  const mlUrl = issue.mailerlite_campaign_id
    ? `https://dashboard.mailerlite.com/campaigns/${issue.mailerlite_campaign_id}/preview`
    : null;
  const scheduledTargetName = issue.scheduled_test ? 'HG Hub Test (just you)' : targetName;

  if (loading) return <div className="text-sm text-hg-text-dim py-6">Loading weekly fitter…</div>;

  return (
    <div className="space-y-6">
      {/* Drafts + AI batch panel */}
      <div className="bg-hg-surface border border-hg-border rounded-sm p-4 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="text-sm font-sans font-semibold text-hg-text uppercase tracking-wider flex items-center gap-2">
            <FileText size={14} className="text-hg-green" /> Drafts
            <span className="text-hg-text-dim font-normal">({drafts.length})</span>
          </h3>
          <div className="flex gap-2">
            <button onClick={newBlank}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans bg-hg-surface border border-hg-border text-hg-text rounded-sm hover:border-hg-green">
              <Plus size={13} /> New blank
            </button>
            <button onClick={fetchDrafts}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans bg-hg-surface border border-hg-border text-hg-text-dim rounded-sm hover:border-hg-green">
              <RefreshCw size={12} /> Refresh
            </button>
          </div>
        </div>

        {/* AI batch generator */}
        <div className="border border-hg-border rounded-sm p-3 space-y-2 bg-hg-bg/40">
          <div className="text-[11px] font-sans uppercase tracking-wider text-hg-green flex items-center gap-1.5">
            <Wand2 size={12} /> AI Batch — draft several weeks at once
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-xs font-sans text-hg-text-dim">How many:</label>
            <select value={batchCount} onChange={(e) => setBatchCount(Number(e.target.value))}
              className="bg-hg-bg border border-hg-border rounded-sm px-2 py-1.5 text-sm font-sans text-hg-text focus:border-hg-green focus:outline-none">
              {[2, 4, 6, 8].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <input value={batchThemes} onChange={(e) => setBatchThemes(e.target.value)}
              placeholder="optional themes (e.g. follow-up, demos, referrals)"
              className="flex-1 min-w-[200px] bg-hg-bg border border-hg-border rounded-sm px-2 py-1.5 text-sm font-sans text-hg-text focus:border-hg-green focus:outline-none" />
            <button onClick={runBatch} disabled={batchGenerating}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-sans bg-hg-green text-hg-text rounded-sm hover:bg-hg-green-hover disabled:opacity-40">
              {batchGenerating ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
              {batchGenerating ? 'Generating…' : `Generate ${batchCount} weeks`}
            </button>
          </div>
          <p className="text-[10px] text-hg-text-dim font-sans flex items-center gap-1">
            <AlertTriangle size={11} /> Drafts only — distinct tips. Review, edit & test each below before scheduling.
          </p>
        </div>

        {/* Draft list */}
        {drafts.length === 0 ? (
          <p className="text-xs text-hg-text-dim font-sans">
            No drafts yet. Use “AI draft this issue,” the batch generator, or run the seed script.
          </p>
        ) : (
          <div className="space-y-1.5 max-h-72 overflow-auto">
            {drafts.map((d) => (
              <button key={d.id} onClick={() => openDraft(d)}
                className={`w-full text-left flex items-center justify-between gap-3 px-3 py-2 rounded-sm border transition-colors ${issue.id === d.id ? 'border-hg-green bg-hg-green/10' : 'border-hg-border hover:border-hg-green'}`}>
                <span className="text-sm font-sans text-hg-text truncate">
                  {d.subject || '(untitled)'}
                  {d.source === 'ai_batch' && <span className="ml-2 text-[9px] uppercase tracking-wider text-hg-green">AI</span>}
                </span>
                <span className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim shrink-0">{draftStatus(d)}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Editor */}
      <div className="bg-hg-surface border border-hg-border rounded-sm p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-sans font-semibold text-hg-text uppercase tracking-wider">Weekly Fitter — Issue Editor</h3>
          {notice && <span className="text-xs text-hg-green font-sans">{notice}</span>}
        </div>

        {/* AI draft this issue (single) */}
        <div className="border border-hg-green/40 rounded-sm p-3 space-y-2 bg-hg-green/5">
          <div className="text-[11px] font-sans uppercase tracking-wider text-hg-green flex items-center gap-1.5">
            <Sparkles size={12} /> AI draft this issue
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input value={aiBrief} onChange={(e) => setAiBrief(e.target.value)}
              placeholder="Optional brief (blank = you decide) — e.g. a tip about reading lie angle"
              className="flex-1 min-w-[220px] bg-hg-bg border border-hg-border rounded-sm px-2 py-1.5 text-sm font-sans text-hg-text focus:border-hg-green focus:outline-none" />
            <button onClick={aiDraft} disabled={aiDrafting}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-sans bg-hg-green text-hg-text rounded-sm hover:bg-hg-green-hover disabled:opacity-40">
              {aiDrafting ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {aiDrafting ? 'Drafting…' : 'AI draft'}
            </button>
          </div>
          <p className="text-[10px] text-hg-text-dim font-sans">
            Fills the fields below as an editable draft. Review & edit, then test — never auto-sent.
          </p>
        </div>

        <Field label="Subject">
          <input className={inputCls} value={issue.subject} onChange={(e) => set('subject', e.target.value)} placeholder="One fitting can change your whole week" />
        </Field>

        {/* Candidate material, named where the issue is actually built. Nothing auto-inserts. */}
        <VideoCandidatesNote where="weekly-fitter" deadlines={['2026-09-07', '2026-09-14']} />
        {/* The Ol' Trusty rollout's dated suggestions for this composer, read from the plan.
            A note, not an insertion: the line goes in only if Randall types it. */}
        <RolloutNote campaignId="ol-trusty-launch" where="weekly-fitter" />

        {/* 1. Header */}
        <div className="border-t border-hg-border pt-4 space-y-4">
          <div className="text-[11px] font-sans uppercase tracking-wider text-hg-green">1 · Header</div>
          <Field label="Rallying headline">
            <input className={inputCls} value={issue.header_headline} onChange={(e) => set('header_headline', e.target.value)} />
          </Field>
          <Field label="Header copy">
            <textarea rows={3} className={inputCls} value={issue.header_body} onChange={(e) => set('header_body', e.target.value)} />
          </Field>
          <EmailImagePanel testIdPrefix="wf-header-img" label="Header image" context="hero" brandCorner
            promptContext={issue.header_headline} imageData={issue.header_image} onImageSet={(v) => set('header_image', v)} />
        </div>

        {/* 2. This Week's Tip */}
        <div className="border-t border-hg-border pt-4 space-y-4">
          <div className="text-[11px] font-sans uppercase tracking-wider text-hg-green">2 · This Week's Tip</div>
          <Field label="Tip lead (bold hook)">
            <input className={inputCls} value={issue.tip_lead} onChange={(e) => set('tip_lead', e.target.value)} placeholder="Stop selling clubs. Start selling the gap." />
          </Field>
          <Field label="Tip body">
            <textarea rows={6} className={inputCls} value={issue.tip_body} onChange={(e) => set('tip_body', e.target.value)} />
          </Field>
        </div>

        {/* 3. What's New (skippable) */}
        <div className="border-t border-hg-border pt-4 space-y-4">
          <div className="text-[11px] font-sans uppercase tracking-wider text-hg-green">3 · What's New at HG</div>
          <Field label="News body" hint="Leave empty to omit this section">
            <textarea rows={3} className={inputCls} value={issue.news_body} onChange={(e) => set('news_body', e.target.value)} placeholder="(optional — skipped when empty)" />
          </Field>
        </div>

        {/* 4. Win of the Week (skippable) */}
        <div className="border-t border-hg-border pt-4 space-y-4">
          <div className="text-[11px] font-sans uppercase tracking-wider text-hg-green">4 · Win of the Week</div>
          <Field label="Win body" hint="Leave empty to omit this section">
            <textarea rows={3} className={inputCls} value={issue.win_body} onChange={(e) => set('win_body', e.target.value)} placeholder="(optional — skipped when empty)" />
          </Field>
          <EmailImagePanel testIdPrefix="wf-win-img" label="Win image" context="email"
            promptContext="fitter spotlight" imageData={issue.win_image} onImageSet={(v) => set('win_image', v)} />
        </div>

        {/* 5. CTA */}
        <div className="border-t border-hg-border pt-4 space-y-4">
          <div className="text-[11px] font-sans uppercase tracking-wider text-hg-green">5 · Call to Action</div>
          <Field label="CTA line"><input className={inputCls} value={issue.cta_text} onChange={(e) => set('cta_text', e.target.value)} /></Field>
          <Field label="CTA supporting blurb"><input className={inputCls} value={issue.cta_blurb} onChange={(e) => set('cta_blurb', e.target.value)} /></Field>
          <Field label="CTA button URL" hint="Optional — adds a button when set"><input className={inputCls} value={issue.cta_url || ''} onChange={(e) => set('cta_url', e.target.value)} placeholder="https://henry-griffitts.com/…" /></Field>
        </div>

        {/* 6. Sign-off */}
        <div className="border-t border-hg-border pt-4 space-y-4">
          <div className="text-[11px] font-sans uppercase tracking-wider text-hg-green">6 · Sign-off</div>
          <Field label="Lead line"><input className={inputCls} value={issue.sign_off_lead} onChange={(e) => set('sign_off_lead', e.target.value)} placeholder="Let's have a great week." /></Field>
          <Field label="Signed" hint='Brand name — not a personal name'>
            <input className={inputCls} value={issue.sign_off} onChange={(e) => set('sign_off', e.target.value)} />
          </Field>
        </div>

        {/* Sending & Scheduling */}
        <div className="border-t border-hg-border pt-4 space-y-3">
          {/* status + real-group target (always explicit) */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-sans px-2 py-1 rounded-sm border border-hg-border text-hg-text-dim">
              {status === 'scheduled' ? <CalendarClock size={12} className="text-hg-green" />
                : status === 'sent' ? <CheckCircle2 size={12} className="text-hg-green" />
                : hasTested ? <CheckCircle2 size={12} className="text-hg-green" /> : null}
              Status:&nbsp;<strong className="text-hg-text">{statusLabel(issue)}</strong>
              {hasTested && status !== 'scheduled' && status !== 'sent' && (
                <span className="text-hg-text-dim">· tested {fmt(issue.test_sent_at)}</span>
              )}
            </span>
            <span className="inline-flex items-center gap-1.5 text-[10px] font-sans text-hg-text-dim">
              <Users size={11} /> Real target: <strong className="text-hg-text">{targetName}</strong>
              {target?.subscriber_count != null && <span>· ~{target.subscriber_count} recipients</span>}
              {target && target.configured === false && <span className="text-amber-500">· not configured</span>}
            </span>
          </div>
          <IssueNotices issue={issue} />

          {/* primary actions */}
          <div className="flex flex-wrap gap-2">
            <button onClick={save} disabled={saving}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-sans bg-hg-surface border border-hg-border text-hg-text rounded-sm hover:border-hg-green disabled:opacity-40">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
            </button>
            <button onClick={preview} disabled={previewing}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-sans bg-hg-surface border border-hg-border text-hg-text rounded-sm hover:border-hg-green disabled:opacity-40">
              {previewing ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />} Preview
            </button>
            <div className="inline-flex items-center gap-1.5">
              <input type="text" value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
                placeholder="you@henry-griffitts.com, grant@…"
                title="Test recipient(s) — defaults to you; separate multiple with commas (each added to HG Hub Test group)"
                className="w-56 bg-hg-bg border border-hg-border rounded-sm px-2 py-2 text-sm font-sans text-hg-text focus:border-hg-green focus:outline-none" />
              <button onClick={sendTest} disabled={sendingTest}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-sans bg-hg-green text-hg-text rounded-sm hover:bg-hg-green-hover disabled:opacity-40 whitespace-nowrap">
                {sendingTest ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send test
              </button>
            </div>
            <button onClick={() => setShowSchedule((s) => !s)} disabled={!hasTested || isScheduled}
              title={!hasTested ? 'Send a test to yourself first' : isScheduled ? 'Already scheduled' : ''}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-sans border border-hg-green text-hg-green rounded-sm hover:bg-hg-green/10 disabled:opacity-40 disabled:cursor-not-allowed">
              <CalendarClock size={14} /> Schedule to fitters…
            </button>
          </div>

          {!hasTested && (
            <p className="text-[10px] text-hg-text-dim font-sans flex items-center gap-1">
              <AlertTriangle size={11} /> Send a test to yourself before you can schedule to fitters.
            </p>
          )}

          {/* schedule panel (only after a test, only when not already scheduled) */}
          {showSchedule && hasTested && !isScheduled && (
            <div className="border border-hg-green/50 rounded-sm p-3 space-y-2 bg-hg-green/5">
              <p className="text-xs font-sans text-hg-text">
                This will schedule a send to{' '}
                <strong>{scheduleToTest ? 'HG Hub Test (just you)' : targetName}</strong>
                {!scheduleToTest && target?.subscriber_count != null && <> (~{target.subscriber_count} recipients)</>} at the time below.
                It stays visible and cancellable in MailerLite until it fires.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <input type="datetime-local" value={sendAt} onChange={(e) => setSendAt(e.target.value)}
                  className="bg-hg-bg border border-hg-border rounded-sm px-2 py-1.5 text-sm font-sans text-hg-text focus:border-hg-green focus:outline-none" />
              </div>
              <label className="flex items-start gap-2 text-[11px] font-sans text-hg-text-dim cursor-pointer">
                <input type="checkbox" checked={scheduleToTest} onChange={(e) => setScheduleToTest(e.target.checked)} className="mt-0.5" />
                <span>Dry-run: schedule to the <strong>HG Hub Test</strong> group (just you), not real fitters — for validating the scheduler.</span>
              </label>
              <label className="flex items-start gap-2 text-xs font-sans text-hg-text cursor-pointer">
                <input type="checkbox" checked={confirmChecked} onChange={(e) => setConfirmChecked(e.target.checked)} className="mt-0.5" />
                <span>I confirm scheduling a send to <strong>{scheduleToTest ? 'HG Hub Test (just you)' : targetName}</strong> at the time above.</span>
              </label>
              <div className="flex gap-2">
                <button onClick={doSchedule} disabled={scheduling || !confirmChecked || !sendAt}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-sans bg-hg-green text-hg-text rounded-sm hover:bg-hg-green-hover disabled:opacity-40 disabled:cursor-not-allowed">
                  {scheduling ? <Loader2 size={14} className="animate-spin" /> : <CalendarClock size={14} />}
                  {scheduleToTest ? 'Confirm & schedule (test)' : 'Confirm & schedule'}
                </button>
                <button onClick={() => { setShowSchedule(false); setConfirmChecked(false); }}
                  className="px-4 py-2 text-sm font-sans border border-hg-border text-hg-text-dim rounded-sm hover:border-hg-green">
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* scheduled state + cancel (prominent confirmation) */}
          {isScheduled && (
            <div className="border-2 border-hg-green rounded-sm p-4 space-y-2 bg-hg-green/10">
              <div className="flex items-center gap-2 text-sm font-sans font-semibold text-hg-text">
                <CalendarClock size={16} className="text-hg-green" /> Scheduled
                {issue.scheduled_test && <span className="text-[10px] font-normal text-hg-text-dim">(test dry-run)</span>}
              </div>
              <p className="text-sm font-sans text-hg-text">
                Sending to <strong>{scheduledTargetName}</strong> on <strong>{fmt(issue.scheduled_at)}</strong>.
              </p>
              <div className="flex flex-wrap gap-2 pt-1">
                {onNavigate && (
                  <button onClick={() => onNavigate('calendar')}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:bg-hg-green-hover">
                    <CalendarClock size={13} /> View on Content Calendar
                  </button>
                )}
                {mlUrl && (
                  <a href={mlUrl} target="_blank" rel="noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-green text-hg-green rounded-sm hover:bg-hg-green/10">
                    <Eye size={13} /> View in MailerLite
                  </a>
                )}
                <button onClick={cancelSchedule} disabled={cancelling}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-red-400 text-red-400 rounded-sm hover:bg-red-400/10 disabled:opacity-40">
                  {cancelling ? <Loader2 size={13} className="animate-spin" /> : <Ban size={13} />} Cancel schedule
                </button>
              </div>
              <p className="text-[10px] text-hg-text-dim font-sans">
                Also visible and cancellable in MailerLite until it sends.
              </p>
            </div>
          )}

          {/* existing draft capability (kept; additive) */}
          <div className="border-t border-hg-border pt-3 flex flex-wrap gap-2">
            <button onClick={() => push(true)} disabled={pushing}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans bg-hg-surface border border-hg-border text-hg-text-dim rounded-sm hover:border-hg-green disabled:opacity-40">
              {pushing ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Push test draft
            </button>
            <button onClick={() => push(false)} disabled={pushing}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans bg-hg-surface border border-hg-border text-hg-text-dim rounded-sm hover:border-hg-green disabled:opacity-40">
              <Send size={13} /> Push to fitters (draft)
            </button>
          </div>
          <p className="text-[10px] text-hg-text-dim font-sans flex items-center gap-1">
            <AlertTriangle size={11} /> Drafts are created in MailerLite for manual review/send. <strong>Scheduling</strong> sends automatically at the chosen time — allowed only after a test + explicit confirm.
          </p>
        </div>
      </div>

      {/* Preview */}
      <div className="bg-hg-surface border border-hg-border rounded-sm p-3">
        {previewHtml ? (
          <iframe title="weekly-fitter-preview" srcDoc={previewHtml} className="w-full h-[80vh] bg-white rounded-sm border border-hg-border" />
        ) : (
          <div className="h-[80vh] flex items-center justify-center text-sm text-hg-text-dim font-sans">
            Click <span className="text-hg-text mx-1">Preview</span> to render the email
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
